import { Box, Flex, Text } from "@chakra-ui/react";
import { Dict, useBooleanState } from "@verivest/shared-components";
import { OpportunityStatusEnumType } from "@verivest/shared-components/src/api/enums";
import { OpportunityModel } from "@verivest/shared-components/src/api/models";
import Button, {
  ButtonProps,
} from "@verivest/shared-components/src/buttons/Button";
import React from "react";
import VeriHeading from "../../../../VeriHeading";
import CardTypeItem from "../../../../general/CardTypeItem";
import { OpportunityTypeSelector } from "../OpportunityTypeSelector";
import {
  getDefinition,
  getValidOfferings,
  opportunitySecurityFormatField,
} from "./OpportunitySecurityCard.helpers";
import { OpportunitySecurityCardProps } from "./OpportunitySecurityCard.types";

export const OpportunitySecurityCard: React.FC<
  OpportunitySecurityCardProps
> = ({ opportunity, isPreview: doNothingOnClick, ...rest }) => {
  const [selectedOffering, setSelectedOffering] = React.useState(0);
  const [selectedSecurity, setSelectedSecurity] = React.useState(0);
  const offerings = getValidOfferings(
    opportunity.offerings || [],
    opportunity.attributeitems || undefined,
    opportunity.status || "",
    opportunity.createdByFundBuilder || false,
  );

  if (
    (!offerings || offerings.length === 0) &&
    !opportunity.createdByFundBuilder
  ) {
    return <></>;
  }
  const activeOffering = offerings[selectedOffering];
  const securities = (activeOffering && activeOffering.securities) || [];
  const activeSecurity = securities[selectedSecurity];
  let definition: Dict = {};
  if (activeSecurity) {
    const investmentPrograms = opportunity.attributeitems
      ?.filter((element) => element.attributeType === "investment_program")
      .map((a) => a.name);
    definition =
      getDefinition(
        opportunity.status || "",
        investmentPrograms,
        activeOffering.offeringType || "",
      ) || {};
  }

  if ((!activeSecurity || !definition) && !opportunity.createdByFundBuilder) {
    return <></>;
  }

  const redirectTo =
    (opportunity: OpportunityModel) =>
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const url = `/w/create/investment/${opportunity.slug || opportunity.id}${
        selectedOffering ? "?offering=" + selectedOffering : ""
      }`;

      window.location.href = url;
    };

  const allowInvesting =
    opportunity.status === OpportunityStatusEnumType.Open &&
    !process.env.RAZZLE_APP_DISABLE_INVEST;

  return (
    <Box className={"mt-6 sm:mt-0"} {...rest}>
      <Box
        style={{
          transition: "all 800ms cubic-bezier(0.19, 1, 0.22, 1)",
        }}
        width="100%"
        maxWidth="450px"
        bg="#FFF"
        className={"shadow-lg border"}
        borderRadius={4}
        position="relative"
        p="24px"
      >
        {offerings.length === 1 && (
          <Flex>
            <OpportunityTypeSelector
              key={"offSelector0"}
              name={offerings[0].shortName}
              active
            />
          </Flex>
        )}
        {offerings.length > 1 && (
          <Flex>
            {offerings.map((o, i) => (
              <OpportunityTypeSelector
                active={selectedOffering === i}
                key={"offSelector" + i}
                name={o.shortName}
                onClick={() => {
                  setSelectedOffering(i);
                  setSelectedSecurity(0);
                }}
              />
            ))}
          </Flex>
        )}
        {securities.length === 1 && (
          <Flex mt="16px">
            <OpportunityTypeSelector
              key={"secSelector0"}
              name={securities[0].shortName}
              active
            />
          </Flex>
        )}
        {securities.length > 1 && (
          <Flex mt="16px" flexWrap={"wrap"}>
            {securities.map((o, i) => (
              <OpportunityTypeSelector
                active={selectedSecurity === i}
                key={"secSelector" + i}
                name={o.shortName}
                onClick={() => setSelectedSecurity(i)}
              />
            ))}
          </Flex>
        )}
        {definition.topFields && (
          <Flex justify="space-between" mt="24px">
            {definition.topFields.map((f: Dict, i: number) => (
              <Box key={"topFields" + i} textAlign="center">
                <VeriHeading textStyle="h5normal">
                  {opportunitySecurityFormatField(
                    f,
                    activeSecurity,
                    opportunity,
                    activeOffering,
                  )}
                </VeriHeading>
                <Text textStyle="bodyXS" color="text.grey">
                  {f.name}
                </Text>
              </Box>
            ))}
          </Flex>
        )}
        <Box mt="12px">
          {definition.fields &&
            definition.fields.map((f: Dict, i: number) => (
              <CardTypeItem
                key={"fields" + i}
                text={f.name}
                value={opportunitySecurityFormatField(
                  f,
                  activeSecurity,
                  opportunity,
                  activeOffering,
                )}
                isLast={i === definition.fields.length - 1}
              />
            ))}
        </Box>
        {allowInvesting &&
          (doNothingOnClick ? (
            <PreviewInvestButton />
          ) : (
            <InvestButton onClick={redirectTo(opportunity)} />
          ))}
      </Box>
      <Box mt="16px">
        <Text as="i">
          There is no guarantee that any performance projections will be
          realized. All private investments involve risks, including the risk of
          complete capital loss. Please see the Offering Memorandum for a more
          complete discussion of risks and expectations for this opportunity.
        </Text>
      </Box>
    </Box>
  );
};

const InvestButton = (props: ButtonProps) => (
  <Button
    fontWeight={500}
    mt="12px"
    label="Invest"
    width="100%"
    isLarge
    {...props}
  />
);

const PreviewInvestButton = () => {
  const hovering = useBooleanState(false);

  return (
    <InvestButton
      onMouseEnter={hovering.true}
      onMouseLeave={hovering.false}
      label={hovering.state ? "Disabled in preview" : "Invest"}
    />
  );
};
